import config from '../../config';
import { getClientData } from '../clientData';
import { getConsent, onConsentChange } from '../consent/consentState';
import { logError } from '../log';
import { isAndroid, isCordova, isIOS } from './deviceDetection';
// ================ Action types ================ //

// ================ Reducer ================ //

const initialState = {
  oneSignalTagsSet: false,
};

export default function reducer(state = initialState, action = {}) {
  const { type } = action;
  switch (type) {
    default:
      return state;
  }
}

// ================ Selectors ================ //

const setOneSignalDebugging = () => {
  // Uncomment to set OneSignal device logging to VERBOSE
  // const OneSignal = window.plugins.OneSignal;
  // OneSignal.Debug.setLogLevel(6);
  // OneSignal.Debug.setAlertLevel(6);
};

// OneSignal attaches its Notifications namespace asynchronously after
// `initialize()`. If a caller hits pushNotificationTrigger before that
// happens we shouldn't permanently skip the permission prompt — wait a
// little and try again. Caps at ~3s total.
const ONE_SIGNAL_READY_MAX_RETRIES = 6;
const ONE_SIGNAL_READY_RETRY_MS = 500;

export const pushNotificationTrigger = async (attempt = 0) => {
  const notifications = window?.plugins?.OneSignal?.Notifications;
  if (!notifications) {
    if (attempt < ONE_SIGNAL_READY_MAX_RETRIES) {
      setTimeout(() => pushNotificationTrigger(attempt + 1), ONE_SIGNAL_READY_RETRY_MS);
    }
    return;
  }

  let hasPushPermission;
  if (isAndroid()) {
    hasPushPermission = await notifications.getPermissionAsync();
  } else if (isIOS()) {
    const res = await notifications.permissionNative();
    hasPushPermission = !(res === 0 || res === 1);
  }
  if (!hasPushPermission) {
    oneSignalAddTrigger('push-notification-permission-prompt', 'true');
  }
};

// ================ Action creators ================ //

// ================ Thunks ================ //

// OneSignal must stay silent until BOTH startup gates are answered: the ToS
// modal AND the cookie-consent startup screen. Granting on ToS alone let
// in-app messages (push-permission prompt, campaigns) pop up on top of the
// consent screen. "Answered" means any decision — declining cookies keeps
// push functional; its own OS-level opt-in governs delivery.
let oneSignalConsentGranted = false;
let oneSignalConsentListenerRegistered = false;

export const oneSignalMaybeGrantConsent = async () => {
  if (!isCordova() || !window?.plugins?.OneSignal) return;
  if (oneSignalConsentGranted) return;
  const tosAccepted = getClientData('vansite.appUsageAccepted') || false;
  if (!tosAccepted || !getConsent()) return;
  oneSignalConsentGranted = true;
  window.plugins.OneSignal.setConsentGiven(true);
  oneSignalInitServices();
  // Seed the campaign-segmentation tag that used to be set on ToS accept —
  // back then the SDK dropped it because consent wasn't given yet.
  try {
    const tags = await window.plugins.OneSignal.User.getTags();
    if (!tags || Object.keys(tags).length === 0) {
      oneSignalAddTags({ registered: false });
    }
  } catch (e) {
    // Tags are segmentation sugar — never fail the consent grant over them.
  }
};

export const oneSignalInit = () => {
  try {
    if (!isCordova() || !window?.plugins?.OneSignal) return;
    console.log('[oneSignal] init');
    setOneSignalDebugging();

    window.plugins.OneSignal.initialize(config.oneSignal.appId);
    window.plugins.OneSignal.setConsentRequired(true);

    if (!oneSignalConsentListenerRegistered) {
      oneSignalConsentListenerRegistered = true;
      onConsentChange(() => oneSignalMaybeGrantConsent());
    }
    oneSignalMaybeGrantConsent();
  } catch (e) {
    logError(e, 'one-signal-init', { message: 'Failed to initialize OneSignal' });
  }
};

export const oneSignalInitServices = () => {
  const locale = navigator.language.split('-')[0];
  oneSignalSharedLocation();
  oneSignalSetLanguage(locale);
  oneSignalAddObservers();
  pushNotificationTrigger();
};

export const oneSignalSharedLocation = async () => {
  if (!isCordova() || !window?.plugins?.OneSignal) return;
  const isShared = await window.plugins.OneSignal.Location.isShared();
  window.plugins.OneSignal.Location.setShared(isShared);
};

export const oneSignalSetLanguage = (locale) => {
  if (!isCordova() || !window?.plugins?.OneSignal) return;
  window.plugins.OneSignal.User.setLanguage(locale);
};


export const oneSignalAddObservers = () => {
  if (!isCordova() || !window?.plugins?.OneSignal) return;
  // The OneSignal plugin attaches its sub-namespaces (Notifications, User,
  // InAppMessages) asynchronously after `initialize()`. If observers are
  // attached before they're ready, the dereferences below throw — see
  // VANSITE-FRONTEND-484/48H/495.
  const notifications = window.plugins.OneSignal.Notifications;
  const user = window.plugins.OneSignal.User;
  if (!notifications || !user?.pushSubscription) return;

  notifications.addEventListener('permissionChange', (granted) => {
    console.log('[oneSignal] permission changed:');
    console.log(granted);
    if (!granted && window.plugins.OneSignal.InAppMessages) {
      window.plugins.OneSignal.InAppMessages.addTrigger(
        'push-notification-permission-prompt',
        'true',
      );
    }
  });

  notifications.addEventListener('click', (jsonData) => {
    const notificationData = JSON.stringify(jsonData);
    console.log('[oneSignal] notification opened:');
    console.dir(notificationData);
  });

  user.pushSubscription.addEventListener('change', (event) => {
    console.log('[oneSignal] subscription changed');
    console.dir(event);
  });
};

export const oneSignalAddTrigger = (key, value) => {
  // InAppMessages is attached asynchronously after initialize() — guard it the
  // same way oneSignalAddObservers does so a too-early call no-ops instead of
  // dereferencing an undefined namespace (VANSITE-FRONTEND-4FW).
  if (!isCordova() || !window?.plugins?.OneSignal?.InAppMessages) return;
  window.plugins.OneSignal.InAppMessages.addTrigger(key, value);
};

export const oneSignalLogin = async (currentUser, currentUserHasListings) => {
  if (!isCordova() || !window?.plugins?.OneSignal) return;
  // Login can reach this point with currentUser still null when the post-login
  // fetchCurrentUser failed (VANSITE-FRONTEND-4KE) — no user, nothing to tag.
  if (!currentUser?.id?.uuid) return;

  window.plugins.OneSignal.login(currentUser.id.uuid);
  const oldTags = await window.plugins.OneSignal.User.getTags();
  if (!oldTags || Object.keys(oldTags).length === 0 || oldTags?.registered !== true) {
    const { attributes } = currentUser;
    const { email, profile, stripeConnected } = attributes;
    const { firstName, lastName, protectedData } = profile;
    oneSignalAddTags({
      email: email,
      first_name: firstName,
      last_name: lastName,
      stripe_connected: stripeConnected,
      is_provider: currentUserHasListings,
      is_potential_provider: !currentUserHasListings && protectedData.approvedForHostIntent,
      receive_news: true,
      registered: true,
    });
  }
};

export const oneSignalLogout = () => {
  if (!isCordova() || !window?.plugins?.OneSignal) return;
  window.plugins.OneSignal.logout();
};

/**
 * @param {object} newTags
 */
export const oneSignalAddTags = (newTags) => {
  if (!isCordova() || !window?.plugins?.OneSignal) return;
  window.plugins.OneSignal.User.addTags(newTags);
};

/**
 * @param {Array<String>} tags
 */
export const oneSignalRemoveTags = (tags) => {
  if (!isCordova() || !window?.plugins?.OneSignal) return;
  window.plugins.OneSignal.User.removeTags(tags);
};
